import { BigNumber } from 'bignumber.js';
import { ApiResult } from '../../../../api/types';
import { EnumOperationType } from '../../../../enum/EnumOperationType';
import { EnumTransactionStatus } from '../../../../enum/EnumTransactionStatus';
import { useTranslation } from '../../../../hooks';
import useChains from '../../../../hooks/useChains';
import { toDisplayTime } from '../../../../utils';
import { GetListRes } from '../types/GetListRes';
import { TableColumnKey, TableRow } from '../types/Table';
import useMerchantList from '../../../../hooks/useMerchantList';
import EnumResultCode from '../../../../enum/EnumResultCode';
import { isNil } from 'lodash';

interface UseTableRowsProps {
  apiList: ApiResult<GetListRes>;
}

type ConvertToTableRowsType = (apiList: ApiResult<GetListRes>) => TableRow[];

const formatDate = (date: Date | string | number) => {
  if (!date) return '';

  return toDisplayTime(date as any);
};

const useTableRows = (props: UseTableRowsProps) => {
  const { apiList } = props;
  const { translate } = useTranslation('transactionRequest');
  const { getChainInfo } = useChains();
  const { getMerchantInfo } = useMerchantList();

  const convertToTableRows: ConvertToTableRowsType = (apiList) => {
    if (!apiList || !apiList?.rows) return [];

    const tableRows = apiList?.rows.map((row): TableRow => {
      const chainInfo = getChainInfo(row.chainType, row.chainId);

      const merchantInfo = getMerchantInfo(row.merchantId);

      const merchantName = merchantInfo ? merchantInfo.name : '';

      return {
        [TableColumnKey.ID]: row.id,
        [TableColumnKey.MERCHANT_NAME]: merchantName,
        [TableColumnKey.BATCH_ID]: row.batchId,
        [TableColumnKey.OPERATION_TYPE]: translate(
          !isNil(row.operationType)
            ? `EnumOperationType.${EnumOperationType[row.operationType]}`
            : ''
        ),
        [TableColumnKey.CHAIN_NAME]: chainInfo?.displayName || '',
        [TableColumnKey.ASSET_NAME]: row?.assetName || '',
        [TableColumnKey.SIGNER_ADDRESS]: row?.signerAddress || '',
        [TableColumnKey.TOTAL_OPERATION]: row?.totalOperation || 0,
        [TableColumnKey.STATUS]: translate(
          !isNil(row?.status) ? `EnumTransactionStatus.${EnumTransactionStatus[row.status]}` : ''
        ),
        [TableColumnKey.RESULT]: `${
          !isNil(row?.resultCode)
            ? translate(`EnumResultCode.${EnumResultCode[row.resultCode]}`)
            : ''
        }`,
        [TableColumnKey.LAST_MODIFIED_BY]: row?.lastModifiedBy || '',
        [TableColumnKey.LAST_MODIFIED_DATE]: formatDate(row?.lastModifiedDate),
        rawData: row,
      };
    });

    return tableRows;
  };

  const rows = convertToTableRows(apiList);

  return { rows, totalCounts: apiList.count, convertToTableRows };
};

export default useTableRows;
